import { useState, useCallback } from "react";
import { View, Text, ScrollView, Pressable, RefreshControl } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";

/**
 * Admin navigation item component
 */
interface AdminNavItemProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  onPress: () => void;
  color?: string;
}

const AdminNavItem = ({
  icon,
  title,
  description,
  onPress,
  color = "#7C3AED",
}: AdminNavItemProps) => {
  const colors = useThemeColors();

  return (
    <Pressable
      onPress={onPress}
      className="rounded-xl p-4 mb-3 flex-row items-center active:opacity-70"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      <View
        className="w-12 h-12 rounded-full items-center justify-center mr-4"
        style={{ backgroundColor: `${color}15` }}
      >
        <Ionicons name={icon} size={24} color={color} />
      </View>
      <View className="flex-1">
        <Text className="text-base font-semibold" style={{ color: colors.text }}>
          {title}
        </Text>
        <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
          {description}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
    </Pressable>
  );
};

/**
 * Admin tab screen
 * Shows admin options based on user roles:
 * - Familie Admin options: only visible when there's an active weekend
 * - Super Admin options: always visible for super admins
 */
export default function AdminTabScreen() {
  const router = useRouter();
  const { roles, activeWeekend, refreshProfile } = useAuth();
  const colors = useThemeColors();
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshProfile();
    } catch (error) {
      console.error("Error refreshing:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshProfile]);

  const handleNavigateToTeams = () => {
    router.push("/(admin)/teams" as any);
  };

  const handleNavigateToAgenda = () => {
    router.push("/agenda" as any);
  };

  const handleNavigateToPunten = () => {
    router.push("/(admin)/punten" as any);
  };

  const handleNavigateToNotifications = () => {
    router.push("/(admin)/notifications" as any);
  };

  const handleNavigateToGames = () => {
    router.push("/(admin)/games" as any);
  };

  const handleNavigateToAlgemeen = () => {
    router.push("/(admin)/algemeen" as any);
  };

  const handleNavigateToUsers = () => {
    router.push("/(super-admin)/users" as any);
  };

  const handleNavigateToWeekends = () => {
    router.push("/(super-admin)/weekends" as any);
  };

  const handleNavigateToLogs = () => {
    router.push("/(super-admin)/logs" as any);
  };

  const handleNavigateToSystemNotifications = () => {
    router.push("/(super-admin)/notifications" as any);
  };

  const handleNavigateToPushTokens = () => {
    router.push("/(super-admin)/notifications/push-tokens" as any);
  };

  const handleNavigateToGamesSettings = () => {
    router.push("/(admin)/games/settings" as any);
  };

  const handleNavigateToPendingMembers = () => {
    router.push("/(super-admin)/pending-members" as any);
  };

  const handleNavigateToGuide = () => {
    router.push("/(admin)/guide" as any);
  };

  // Familie admin options only shown when there's an active weekend
  const showFamilieAdminOptions = roles.isFamilieAdmin && activeWeekend !== null;

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top"]}
    >
      <ScrollView
        className="flex-1"
        contentContainerClassName="px-4 py-6"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        {/* Header */}
        <View className="mb-6">
          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            Admin
          </Text>
          <Text className="mt-1" style={{ color: colors.textSecondary }}>
            Beheer het familieweekend
          </Text>
        </View>

        {/* Active Weekend Banner */}
        {activeWeekend && (
          <View
            className="rounded-xl p-4 mb-6 flex-row items-center"
            style={{ backgroundColor: colors.primaryLight }}
          >
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: `${colors.primary}20` }}
            >
              <Ionicons name="calendar" size={20} color={colors.primary} />
            </View>
            <View className="flex-1">
              <Text className="text-sm font-medium" style={{ color: colors.primary }}>
                Actief weekend
              </Text>
              <Text className="font-semibold" style={{ color: colors.text }}>
                {activeWeekend.name}
              </Text>
            </View>
          </View>
        )}

        {/* Familie Admin - No active weekend message */}
        {roles.isFamilieAdmin && !activeWeekend && (
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: "#FEF3C7", borderWidth: 1, borderColor: "#FCD34D" }}
          >
            <View className="flex-row items-center mb-3">
              <View className="w-10 h-10 rounded-full bg-amber-100 items-center justify-center mr-3">
                <Ionicons name="calendar-outline" size={20} color="#F59E0B" />
              </View>
              <Text className="text-amber-800 font-semibold text-base">
                Geen actief weekend
              </Text>
            </View>
            <Text className="text-amber-700 text-sm leading-5">
              Familie admin opties zijn alleen beschikbaar wanneer er een weekend actief is.
            </Text>
            <View className="bg-amber-100 rounded-lg p-3 mt-3 flex-row items-start">
              <Ionicons name="bulb-outline" size={18} color="#92400E" />
              <Text className="text-amber-800 text-sm ml-2 flex-1">
                Vraag een super admin om een familieweekend te activeren zodat je aan de slag kunt!
              </Text>
            </View>
          </View>
        )}

        {/* Familie Admin Options - Only visible when there's an active weekend */}
        {showFamilieAdminOptions && (
          <View className="mb-6">
            <Text
              className="text-sm font-semibold uppercase tracking-wide mb-3"
              style={{ color: colors.textTertiary }}
            >
              Familie Admin
            </Text>

            <AdminNavItem
              icon="settings-outline"
              title="Algemeen"
              description="Weekend naam, thema en banner instellen"
              onPress={handleNavigateToAlgemeen}
              color="#D97706"
            />
            
            <AdminNavItem
              icon="people-outline"
              title="Teams beheren"
              description="Teams aanmaken en leden toewijzen"
              onPress={handleNavigateToTeams}
            />
            
            <AdminNavItem
              icon="trophy-outline"
              title="Punten toekennen"
              description="Scores bijwerken voor teams en individuen"
              onPress={handleNavigateToPunten}
              color="#F97316"
            />
            
            <AdminNavItem
              icon="calendar-outline"
              title="Agenda beheren"
              description="Evenementen toevoegen en bewerken"
              onPress={handleNavigateToAgenda}
            />
            
            <AdminNavItem
              icon="game-controller-outline"
              title="Spellen beheren"
              description="Spelregels en configuratie"
              onPress={handleNavigateToGames}
            />
            
            <AdminNavItem
              icon="notifications-outline"
              title="Familie notificaties"
              description="Automatische meldingen beheren"
              onPress={handleNavigateToNotifications}
              color="#10B981"
            />

            <AdminNavItem
              icon="book-outline"
              title="📖 Handleiding"
              description="Stap-voor-stap uitleg voor alle functies"
              onPress={handleNavigateToGuide}
              color="#6366F1"
            />
          </View>
        )}

        {/* Super Admin Options - Only visible to super admins */}
        {roles.isSuperAdmin && (
          <View className="mb-6">
            <Text
              className="text-sm font-semibold uppercase tracking-wide mb-3"
              style={{ color: colors.textTertiary }}
            >
              Super Admin
            </Text>
            
            <AdminNavItem
              icon="people-outline"
              title="Gebruikers beheren"
              description="Bekijk, beheer en wijzig rollen"
              onPress={handleNavigateToUsers}
              color="#EF4444"
            />
            
            <AdminNavItem
              icon="person-add-outline"
              title="Pre-registraties"
              description="Familieleden toevoegen voordat ze zich aanmelden"
              onPress={handleNavigateToPendingMembers}
              color="#EF4444"
            />
            
            <AdminNavItem
              icon="calendar-number-outline"
              title="Weekenden beheren"
              description="Nieuw weekend aanmaken of archiveren"
              onPress={handleNavigateToWeekends}
              color="#EF4444"
            />
            
            <AdminNavItem
              icon="document-text-outline"
              title="Activiteiten log"
              description="Bekijk alle acties in de app"
              onPress={handleNavigateToLogs}
              color="#EF4444"
            />
            
            <AdminNavItem
              icon="notifications-outline"
              title="Systeem notificaties"
              description="Rol toewijzing, nieuwe weekenden, features"
              onPress={handleNavigateToSystemNotifications}
              color="#EF4444"
            />
            
            <AdminNavItem
              icon="game-controller-outline"
              title="Spellen instellingen"
              description="Lottie animaties en standaardwaarden"
              onPress={handleNavigateToGamesSettings}
              color="#EF4444"
            />

            <AdminNavItem
              icon="hardware-chip-outline"
              title="Push Token Status"
              description="Bekijk push tokens en stuur test notificaties"
              onPress={handleNavigateToPushTokens}
              color="#EF4444"
            />
          </View>
        )}

        {/* Empty state if user has admin tab but no specific permissions */}
        {!roles.isFamilieAdmin && !roles.isSuperAdmin && (
          <View className="flex-1 items-center justify-center py-12">
            <Ionicons name="lock-closed-outline" size={48} color={colors.border} />
            <Text className="mt-2 text-center" style={{ color: colors.textTertiary }}>
              Je hebt geen admin rechten
            </Text>
          </View>
        )}

        {/* Pull to refresh hint */}
        <View className="items-center py-4">
          <Text className="text-xs" style={{ color: colors.textTertiary }}>
            Trek naar beneden om te vernieuwen
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
